import React, { useEffect, useRef, useState } from 'react';
import { TYPE } from '../../constants/typeScale';

let _seq = 0;

/**
 * Renders a Mermaid diagram from a ```mermaid fenced code block. Mermaid is LAZY-loaded (dynamic
 * import) so it never bloats the main bundle and never breaks jest (the import only runs when a
 * diagram actually renders). On invalid syntax we fall back to the raw code, so a bad diagram never
 * shows Mermaid's "Syntax error in text" graphic in the chat. Theme follows the app (monochrome-ish).
 */
const MermaidDiagram: React.FC<{ code: string }> = ({ code }) => {
  const [svg, setSvg] = useState('');
  const [failed, setFailed] = useState(false);
  const idRef = useRef(`mermaid-${++_seq}`);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
        const mermaid = (await import('mermaid')).default;
        mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: isDark ? 'dark' : 'neutral' });
        // VALIDATE FIRST. render() emits a "Syntax error in text" SVG (and appends a stray error
        // node to <body>) instead of throwing on bad input — which leaked into the chat. parse() with
        // suppressErrors returns false on invalid syntax WITHOUT that side effect, so we can fall back
        // cleanly to the raw code.
        const valid = await mermaid.parse(code, { suppressErrors: true });
        if (!valid) { if (alive) setFailed(true); return; }
        const { svg } = await mermaid.render(idRef.current, code);
        if (alive) { setSvg(svg); setFailed(false); }
      } catch {
        if (alive) setFailed(true);
      } finally {
        // Belt-and-braces: remove any stray error/render node Mermaid may have appended to <body>.
        try { document.getElementById(idRef.current)?.remove(); document.getElementById(`d${idRef.current}`)?.remove(); } catch { /* ignore */ }
      }
    })();
    return () => { alive = false; };
  }, [code]);

  if (failed) {
    return (
      <pre className={`overflow-x-auto rounded-lg bg-secondary-900 dark:bg-black p-3 ${TYPE.control} text-secondary-100`} data-testid="mermaid-fallback">
        {code}
      </pre>
    );
  }
  if (!svg) return <div className="my-1.5 h-24 w-full animate-pulse rounded-lg bg-secondary-100 dark:bg-secondary-800" data-testid="mermaid-loading" />;
  return (
    <div
      className="my-1.5 max-w-full overflow-x-auto rounded-xl border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-900/40 p-2.5 [&_svg]:max-w-full [&_svg]:h-auto"
      data-testid="mermaid-diagram"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

export default MermaidDiagram;
